import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addCareRoundFailure,
  addCareRoundSuccess,
  deleteCareRoundFailure,
  deleteCareRoundSuccess,
  editCareRoundFailure,
  editCareRoundSuccess,
  fetchCareRoundFailure,
  fetchCareRoundItemFailure,
  fetchCareRoundItemSuccess,
  fetchCareRoundSuccess,
} from "./actions";
import {
  ADD_CARE_ROUND,
  DELETE_CARE_ROUND,
  EDIT_CARE_ROUND,
  FETCH_CARE_ROUND,
  FETCH_CARE_ROUND_ITEM,
} from "./constants";

const SETTINGS_PATH = "/d/r/settings/care-rounds";

const extractFieldErrors = (errorLike) => {
  const raw = errorLike?.response?.data;
  const messageBag =
    raw?.message &&
    typeof raw.message === "object" &&
    !Array.isArray(raw.message)
      ? raw.message
      : raw?.error && typeof raw.error === "object" && !Array.isArray(raw.error)
        ? raw.error
        : null;
  if (!messageBag) return null;
  const fieldErrors = {};
  Object.entries(messageBag).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      fieldErrors[key] = value.filter(Boolean).join(", ");
    } else if (typeof value === "string") {
      fieldErrors[key] = value;
    } else if (value != null) {
      fieldErrors[key] = String(value);
    }
  });
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : null;
};

export function* fetchCareRoundSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `/api/round?${query}`);
    const result = Array.isArray(data)
      ? { data, total: data.length, current_page: payload?.page ?? 1 }
      : { ...data, current_page: payload?.page ?? data?.current_page ?? 1 };
    yield put(fetchCareRoundSuccess(result));
  } catch (e) {
    yield put(fetchCareRoundFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care rounds"));
  }
}

export function* addCareRoundSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, onSuccess, onError } = payload || {};
  try {
    const response = yield call(api(token).post, `/api/round/add`, data);
    if (response) {
      yield put(addCareRoundSuccess());
      yield put(successAlert("Care round created successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(addCareRoundFailure());
    const fieldErrors = extractFieldErrors(e);
    if (fieldErrors && typeof onError === "function") {
      onError(fieldErrors);
      return;
    }
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to create care round"));
  }
}

export function* fetchCareRoundItemSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id =
    typeof payload === "string" ? payload : (payload?.uuid ?? payload?.id);
  try {
    const { data } = yield call(api(token).get, `/api/round/${id}`);
    yield put(fetchCareRoundItemSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchCareRoundItemFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care round"));
  }
}

export function* editCareRoundSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, id, uuid, onSuccess, onError } = payload || {};
  const itemId = id ?? uuid;
  try {
    const response = yield call(
      api(token).post,
      `/api/round/edit/${itemId}`,
      data,
    );
    if (response) {
      yield put(editCareRoundSuccess());
      yield put(successAlert("Care round updated successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(editCareRoundFailure());
    const fieldErrors = extractFieldErrors(e);
    if (fieldErrors && typeof onError === "function") {
      onError(fieldErrors);
      return;
    }
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to update care round"));
  }
}

export function* deleteCareRoundSaga({ payload, onDelete }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id = payload?.id ?? payload?.uuid;
  try {
    const data = yield call(api(token).delete, `/api/round/${id}`);
    if (data) {
      yield put(deleteCareRoundSuccess());
      yield put(successAlert("Care round deleted successfully"));
      typeof onDelete === "function" && onDelete();
    }
  } catch (e) {
    yield put(deleteCareRoundFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to delete care round"));
  }
}

export default function* CareRoundSaga() {
  yield all([
    takeEvery(FETCH_CARE_ROUND, fetchCareRoundSaga),
    takeEvery(ADD_CARE_ROUND, addCareRoundSaga),
    takeEvery(FETCH_CARE_ROUND_ITEM, fetchCareRoundItemSaga),
    takeEvery(EDIT_CARE_ROUND, editCareRoundSaga),
    takeEvery(DELETE_CARE_ROUND, deleteCareRoundSaga),
  ]);
}
